import { Chat, FirstAidKit } from '@phosphor-icons/react';
import useApiPromises from 'api/hooks/useApiPromises';
import classNames from 'classnames';
import { useAccount } from 'context/AccountContext';
import { useOrganization } from 'context/OrganizationContext';
import { MareCycleCheck, MarecyclecheckService } from 'openapi';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  table,
  tableHiddenColumnMd,
  tableHiddenHeaderSm,
  tableTbody,
  tableTbodyTrNoClick,
  tableTheadTd,
  tableTheadTdSticky,
} from 'ui/Const';
import { Spinner } from 'ui/Loading';
import { SpinnerSize } from 'ui/Loading/Spinner';
import { PageModal } from 'ui/Modals';
import { PageModalContent, PageModalTitle } from 'ui/Modals/PageModal';
import Tooltip from 'ui/Tooltip';
import { ApiPromises } from 'utilities/ApiPromises';
import {
  eggLocationEnumToString,
  eggStateEnumToString,
  pregnancyCheckIsPregnantEnumToString,
  softnessEnumToString,
  uterusEnumToString,
} from 'utilities/Breeding';

interface Props {
  isVisible: boolean;
  onRequestCloseModal: () => void;
  mareUid: string;
}

function ShowPreviousMareCycleChecksModal({ isVisible, onRequestCloseModal, mareUid }: Props): JSX.Element {
  const [apiPromises, setApiPromises] = useState<ApiPromises>();
  const [mareCycleChecks, setMareCycleChecks] = useState<MareCycleCheck[]>();

  const { loading } = useApiPromises({ apiPromises });
  const { t } = useTranslation();
  const { selectedOrganization, generateCacheKey } = useOrganization();
  const { formatDate } = useAccount();

  /**
   * Load data from the api/cache
   */
  const loadApiData = useCallback((): ApiPromises => {
    const promises = new ApiPromises();

    if (!selectedOrganization) {
      return promises;
    }

    promises.appendList(
      `mare-cycle-checks-${mareUid}`,
      () =>
        MarecyclecheckService.marecyclecheckList({
          mareOrganisationUid: selectedOrganization.uid,
          mareUid,
          o: '-date,id',
        }),
      setMareCycleChecks,
      generateCacheKey(`mare-cycle-checks-${mareUid}`),
    );

    setApiPromises(promises);
    return promises;
  }, [generateCacheKey, mareUid, selectedOrganization]);

  /**
   * Load from the api
   */
  useEffect(() => {
    if (selectedOrganization && isVisible) {
      const promise = loadApiData();
      return () => promise.cancel();
    }
  }, [selectedOrganization, isVisible]); //eslint-disable-line

  return (
    <PageModal open={isVisible} parentElement='form'>
      <PageModalTitle title={t('mare-cycle-history', 'Mare cycle history')} onClose={onRequestCloseModal} />
      <PageModalContent>
        <table className={table}>
          <thead>
            <tr className={tableHiddenHeaderSm}>
              <td className={classNames('w-10', tableTheadTdSticky)} />
              <td className={tableTheadTdSticky}>{t('date', 'Date')}</td>
              <td className={classNames(tableTheadTd, tableTheadTdSticky)}>{t('pregnant', 'Pregnant')}</td>
              <td className={classNames(tableTheadTd, tableTheadTdSticky)}>{t('uterus', 'Uterus')}</td>
              <td className={classNames(tableTheadTd, tableTheadTdSticky)}>{t('cervix', 'Cervix')}</td>
              <td className={classNames(tableTheadTd, tableTheadTdSticky)}>{t('eggs', 'Eggs')}</td>
            </tr>
          </thead>
          <tbody className={tableTbody}>
            {loading && (
              <td colSpan={4}>
                <div className='pt-3 flex items-center gap-x-2'>
                  <Spinner size={SpinnerSize.XSmall} />
                  <p>
                    {t('loading', 'Loading')}
                    {'...'}
                  </p>
                </div>
              </td>
            )}
            {(mareCycleChecks ?? []).map(mareCycleCheck => {
              return (
                <tr className={tableTbodyTrNoClick} key={mareCycleCheck.uid}>
                  <td className='text-center w-10'>
                    <FirstAidKit size={24} weight='light' className='inline' />
                  </td>
                  <td>
                    <div className='flex gap-x-1 items-center'>
                      {mareCycleCheck.date && formatDate(new Date(Date.parse(mareCycleCheck.date)))}
                      {mareCycleCheck.extra_info && (
                        <Tooltip content={mareCycleCheck.extra_info} classNameContent='max-w-[300px]'>
                          <div className='flex gap-x-1'>
                            <Chat size={16} />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  </td>

                  <td className={tableHiddenColumnMd}>{pregnancyCheckIsPregnantEnumToString(mareCycleCheck.is_pregnant)}</td>
                  <td className={tableHiddenColumnMd}>{uterusEnumToString(mareCycleCheck.uterus)}</td>
                  <td className={tableHiddenColumnMd}>{softnessEnumToString(mareCycleCheck.cervix)}</td>
                  <td className={tableHiddenColumnMd}>
                    {mareCycleCheck.egg_set && (
                      <>
                        {mareCycleCheck.egg_set.map(egg => (
                          <div key={egg.uid} className='flex gap-x-1'>
                            {eggLocationEnumToString(egg.location)}{' '}
                            {egg.size_metric && (
                              <>
                                - {egg.size_metric} {t('cm-metric', 'cm')}
                              </>
                            )}{' '}
                            {egg.hardness && <>- {softnessEnumToString(egg.hardness)}</>}
                            {egg.egg_state && <>- {eggStateEnumToString(egg.egg_state)}</>}
                          </div>
                        ))}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </PageModalContent>
    </PageModal>
  );
}

export default ShowPreviousMareCycleChecksModal;
